import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import React, { useMemo, useState } from 'react';
import { RadioButtons } from '@dapp/multi-lib/sdaoui/RadioButtons';
import { ChartLine } from 'structure/pv2/vault/components/ChartLine';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { VaultIdentity } from 'structure/pv2/vault/utils/vault';
import { useVaultStats } from 'structure/pv2/vault/hooks/useVault';
import { NetworkChainIds } from '@dapp/multi-lib/network';
import { daysToSeconds } from 'utils/date';

interface VaultChartProps {
  vaultIdentity: VaultIdentity;
}

const msInDay = daysToSeconds(1) * 1000;

const yLabelPrecisionMap = {
  price: 6,
  tvl: 2,
  performance: 4,
};

export const VaultChart = (props: VaultChartProps) => {
  const { vaultIdentity } = props;

  const [chartType, setChartType] = useState<'price' | 'tvl' | 'performance'>(
    'price'
  );
  const [timeframe, setTimeframe] = useState<'1d' | '1w'>('1d');

  const msTimestamp = useMemo(() => {
    if (timeframe === '1d') {
      return Date.now() - msInDay;
    }

    return Date.now() - msInDay * 7;
  }, [timeframe]);

  const statsQuery = useVaultStats({
    address: vaultIdentity.address as `0x${string}`,
    chainId: vaultIdentity.chainId as NetworkChainIds,
    fromTimestamp: msTimestamp / 1000,
    price: true,
    tvl: true,
    query: {
      refetchInterval: 1000 * 60 * 5,
    },
  });

  const csr = useClientRenderDelay();

  const data = useMemo(() => {
    if (!statsQuery.data) {
      return [];
    }

    if (chartType === 'price') {
      return statsQuery.data?.map((stat) => stat.sharePriceInUSD);
    }

    if (chartType === 'tvl') {
      return statsQuery.data?.map((stat) => parseFloat(stat.tvl));
    }

    return statsQuery.data?.map((stat) => parseFloat(stat.netPerformance));
  }, [statsQuery.data, chartType]);

  const labels = useMemo(() => {
    if (!statsQuery.data) {
      return [];
    }

    return statsQuery.data.map((stat) => stat.timestamp);
  }, [statsQuery.data]);

  const maxTicksLimit = timeframe === '1d' ? 12 : 7;
  const unit = timeframe === '1d' ? 'hour' : 'day';

  const denomination = chartType === 'performance' ? '%' : '$';

  const yLabelPrecision = yLabelPrecisionMap[chartType];

  return (
    <Surface
      rounded='large'
      className='bg-linear-gradient-black flex h-full flex-col justify-between border-[0.6px] border-solid border-[rgba(97,_111,_139,_0.50)] px-5 py-9'
    >
      <div className='mb-6 flex flex-row justify-between'>
        <RadioButtons<'tvl' | 'price' | 'performance'>
          value={chartType}
          size='small'
          onChange={(value) => setChartType(value)}
          options={[
            { label: 'Price', value: 'price' },
            { label: 'TVL', value: 'tvl' },
            { label: 'Performance', value: 'performance' },
          ]}
        />

        <RadioButtons<'1d' | '1w'>
          value={timeframe}
          size='small'
          onChange={(value) => setTimeframe(value)}
          options={[
            { label: '1D', value: '1d' },
            { label: '1W', value: '1w' },
          ]}
        />
      </div>

      <div className='flex grow flex-col justify-end'>
        {csr ? (
          <ChartLine
            data={data}
            unit={unit}
            labels={labels}
            maxTicksLimit={maxTicksLimit}
            denomination={denomination}
            yLabelPrecision={yLabelPrecision}
          />
        ) : null}
      </div>
    </Surface>
  );
};
